import React, { useState, useCallback } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { Form } from "react-bootstrap";
import { HOUR, renderPercentChange } from "./helper";
import { fourPlaceHolder } from "modules/utilities/utility";

function ConversionDashboard({ loading, todayFormData, yestFormData }) {
  const [selectedGraphs, setSelectedGraphs] = useState({
    formFill: true,
    conversion: true,
    abandonedCarts: true,
  });

  const getOption = useCallback(
    (
      totalFormFillToday,
      totalFormFillYesterday,
      totalConversionToday,
      totalConversionYesterday,
      totalAbandonedCartsToday,
      totalAbandonedCartsYesterday
    ) => ({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          lineStyle: {
            color: "#b0b0b0",
            width: 1,
          },
        },
        padding: [7, 10],
        backgroundColor: "#ffffff",
        borderColor: "#b0b0b0",
        borderWidth: 1,
        textStyle: {
          color: "#000000",
          fontSize: 12,
        },
        formatter: (params) => {
          const todayFormFillValue = totalFormFillToday[params[0].dataIndex];
          const yestFormFillValue = totalFormFillYesterday[params[0].dataIndex];
          const todayConversionValue =
            totalConversionToday[params[0].dataIndex];
          const yestConversionValue =
            totalConversionYesterday[params[0].dataIndex];
          const todayAbandonValue = totalConversionToday[params[0].dataIndex];
          const yestAbandonValue =
            totalConversionYesterday[params[0].dataIndex];

          let tooltipContent = `<b>${params[0].axisValue}</b><br/>`;

          if (selectedGraphs.formFill) {
            tooltipContent += `
              <span style="color:#007bff;">● Form Fill Today:</span> ${todayFormFillValue}<br/>
              <span style="color:#007bff;">● Form Fill Yesterday:</span> ${yestFormFillValue}<br/>
            `;
          }

          if (selectedGraphs.conversion) {
            tooltipContent += `
              <span style="color: #28a745;">● Conversion Today:</span> ${todayConversionValue}<br/>
              <span style="color: #28a745;">● Conversion Yesterday:</span> ${yestConversionValue}<br/>
            `;
          }

          if (selectedGraphs.abandonedCarts) {
            tooltipContent += `
              <span style="color:#ffc107;">● Abandoned Today:</span> ${todayAbandonValue}<br/>
              <span style="color:#ffc107;">● Abandoned Yesterday:</span> ${yestAbandonValue}<br/>
            `;
          }

          return tooltipContent;
        },
      },
      xAxis: {
        type: "category",
        data: HOUR,
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: "#b0b0b0",
          },
        },
        axisLabel: {
          color: "#000000",
          fontSize: 12,
        },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: {
            color: "#e0e0e0",
          },
        },
        axisLabel: {
          color: "#000000",
          fontSize: 12,
        },
      },
      series: [
        selectedGraphs.formFill && {
          name: "Form Fill Today",
          type: "line",
          smooth: true,
          data: totalFormFillToday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#0d6efd" },
          lineStyle: { color: "#0d6efd", width: 2 },
        },
        selectedGraphs.formFill && {
          name: "Form Fill Yesterday",
          type: "line",
          smooth: true,
          data: totalFormFillYesterday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#0d6efd" },
          lineStyle: { color: "#0d6efd", width: 2, type: "dotted" },
        },
        selectedGraphs.conversion && {
          name: "Conversion Today",
          type: "line",
          smooth: true,
          data: totalConversionToday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#198754" },
          lineStyle: { color: "#198754", width: 2 },
        },
        selectedGraphs.conversion && {
          name: "Conversion Yesterday",
          type: "line",
          smooth: true,
          data: totalConversionYesterday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#198754" },
          lineStyle: { color: "#198754", width: 2, type: "dotted" },
        },
        selectedGraphs.abandonedCarts && {
          name: "Abandoned Carts Today",
          type: "line",
          smooth: true,
          data: totalAbandonedCartsToday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#ffc107" },
          lineStyle: { color: "#ffc107", width: 2 },
        },
        selectedGraphs.abandonedCarts && {
          name: "Abandoned Carts Yesterday",
          type: "line",
          smooth: true,
          data: totalAbandonedCartsYesterday,
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#ffc107" },
          lineStyle: { color: "#ffc107", width: 2, type: "dotted" },
        },
      ].filter(Boolean),
      grid: {
        left: "5%",
        right: "5%",
        bottom: "10%",
        top: "5%",
        containLabel: true,
      },
    }),
    [selectedGraphs] 
  );

  const handleCheckboxChange = (checkboxId) => {
    setSelectedGraphs((prevCheckboxes) => ({
      ...prevCheckboxes,
      [checkboxId]: !prevCheckboxes[checkboxId],
    }));
  };

  return loading ? (
    <div className="mt-5">{fourPlaceHolder()}</div>
  ) : (
    <div className="">
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-2 mb-2 fw-bold ">Conversion Graph <span className="small">for New Customer</span></div>
        <div className="d-flex flex-row gap-3 mb-n2">
          <Form.Check
            type="checkbox"
            id="formFill"
            className="mb-0 d-flex ps-0"
          >
            <Form.Check.Input
              type="checkbox"
              className="ms-0 form-check-input-primary opacity-75"
              onClick={() => handleCheckboxChange("formFill")}
              checked={selectedGraphs.formFill}
            />
            <Form.Check.Label className="ps-2 text-600 mb-0">
              Form Filled
            </Form.Check.Label>
          </Form.Check>
          <Form.Check
            type="checkbox"
            id="conversion"
            className="mb-0 d-flex ps-0"
          >
            <Form.Check.Input
              type="checkbox"
              className="ms-0 form-check-input-success opacity-75"
              onClick={() => handleCheckboxChange("conversion")}
              checked={selectedGraphs.conversion}
            />
            <Form.Check.Label className="ps-2 text-600 mb-0">
              Conversion
            </Form.Check.Label>
          </Form.Check>
          <Form.Check
            type="checkbox"
            id="abandonedCarts"
            className="mb-0 d-flex ps-0"
          >
            <Form.Check.Input
              type="checkbox"
              className="ms-0 form-check-input-warning opacity-75"
              onClick={() => handleCheckboxChange("abandonedCarts")}
              checked={selectedGraphs.abandonedCarts}
            />
            <Form.Check.Label className="ps-2 text-600 mb-0">
              Abandoned Cart
            </Form.Check.Label>
          </Form.Check>
        </div>
      </div>
      <div className="d-flex fle-row gap-2">
        <div className="d-flex flex-column gap-3">
          <div
            className={`card shadow-sm p-2 rounded-3 cursor-pointer transition-all duration-300`}
          >
            <div className=" fw-bold mb-2">Total FormFill</div>
            <div className="fw-bold  text-dark mb-2 text-nowrap">
              {todayFormData.totalFormSum}{" "}
              <span className="fw-semibold text-muted ms-2">
                {yestFormData.totalFormSum}
              </span>
              <span className="ms-2">
                {renderPercentChange(
                  todayFormData.totalFormSum,
                  yestFormData.totalFormSum
                )}
              </span>
            </div>
          </div>
          <div
            className={`card shadow-sm p-2 rounded-3 cursor-pointer transition-all duration-300`}
          >
            <div className=" fw-bold mb-2">Total Conversion</div>
            <div className="fw-bold  text-dark mb-2 text-nowrap">
              {todayFormData.totalConversionSum}{" "}
              <span className="fw-semibold text-muted ms-2">
                {yestFormData.totalConversionSum}
              </span>
              <span className="ms-2">
                {renderPercentChange(
                  todayFormData.totalConversionSum,
                  yestFormData.totalConversionSum
                )}
              </span>
            </div>
          </div>
          <div
            className={`card shadow-sm p-2 rounded-3 cursor-pointer transition-all duration-300`}
          >
            <div className=" fw-bold mb-2 text-nowrap">Total Abandoned</div>
            <div className="fw-bold  text-dark mb-2 text-nowrap">
              {todayFormData.totalAbandonSum}{" "}
              <span className="fw-semibold text-muted ms-2">
                {yestFormData.totalAbandonSum}
              </span>
              <span className="ms-2">
                {renderPercentChange(
                  todayFormData.totalAbandonSum,
                  yestFormData.totalAbandonSum
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="flex-3 mt-n2">
          <ReactEChartsCore
            key={JSON.stringify(getOption())}
            echarts={echarts}
            option={getOption(
              todayFormData.totalFormFill,
              yestFormData.totalFormFill,
              todayFormData.totalConversion,
              yestFormData.totalConversion,
              todayFormData.totalAbandonedCarts,
              yestFormData.totalAbandonedCarts
            )}
            style={{ height: "25rem" }}
            className="rounded-4"
          />
        </div>
      </div>
    </div>
  );
}

export default ConversionDashboard;
